import { z } from 'zod';
import { CurrencySchema } from './money';
import { COMMISSION_KINDS, COMMISSION_STATUSES } from './referral';

/**
 * 分销渠道（多租户，docs/12-分销渠道与模型路由需求.md）。
 * 结算模式「平台代收、按折扣分成」：客户照常向平台付费；渠道按折扣拿货，
 * 每笔任务结算时计提 渠道分成 = 实收 − 实收 × 折扣，平台定期结算给渠道。
 *
 * 折扣（discount_pct）= 批发价占刊例价的百分比：80 表示 8 折拿货，渠道分成 20%（share_pct）。
 */

/** 渠道推广码：注册链接 /register?ch=CODE；4–16 位大写字母或数字 */
export const RESELLER_CODE_RE = /^[A-Z0-9]{4,16}$/;

export function normalizeResellerCode(input: string | null | undefined): string | null {
  if (!input) return null;
  const v = input.replace(/\s+/g, '').toUpperCase();
  return RESELLER_CODE_RE.test(v) ? v : null;
}

export const RESELLER_STATUSES = ['active', 'suspended', 'closed'] as const;
export type ResellerStatus = (typeof RESELLER_STATUSES)[number];
export const RESELLER_ROLES = ['owner', 'staff'] as const;
export type ResellerRole = (typeof RESELLER_ROLES)[number];
export const RESELLER_SETTLEMENT_METHODS = ['offline', 'quota'] as const;
export type ResellerSettlementMethod = (typeof RESELLER_SETTLEMENT_METHODS)[number];

/** 百分比，最多 2 位小数 */
export const DiscountPctSchema = z
  .number()
  .min(0)
  .max(100)
  .refine((v) => Math.abs(v * 100 - Math.round(v * 100)) < 1e-6, '最多保留 2 位小数');

export const pctToBps = (pct: number) => Math.round(pct * 100);
export const bpsToPct = (bps: number) => bps / 100;

export const ModelDiscountSchema = z.object({
  model: z.string().trim().min(1).max(64),
  discount_pct: DiscountPctSchema,
});
export type ModelDiscount = z.infer<typeof ModelDiscountSchema>;

/** 渠道信息（渠道后台与管理后台共用） */
export const ResellerInfoSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  status: z.enum(RESELLER_STATUSES),
  /** 推广注册链接 */
  link: z.string(),
  discount_pct: z.number(),
  share_pct: z.number(),
  model_discounts: z.array(ModelDiscountSchema.extend({ share_pct: z.number() })),
  currency: CurrencySchema,
});
export type ResellerInfo = z.infer<typeof ResellerInfoSchema>;

export const ResellerMonthSchema = z.object({
  /** YYYY-MM（站点时区） */
  month: z.string(),
  /** 客户实付（刊例价） */
  charged: z.string(),
  wholesale: z.string(),
  /** 渠道分成（已扣除冲回） */
  amount: z.string(),
  tasks: z.number(),
});
export type ResellerMonth = z.infer<typeof ResellerMonthSchema>;

// ───────────── 渠道后台（控制台 /portal/reseller） ─────────────

export const ResellerPortalSummarySchema = z.object({
  reseller: ResellerInfoSchema,
  role: z.enum(RESELLER_ROLES),
  customers: z.number(),
  /** 近 30 天新增客户 */
  customers_30d: z.number(),
  /** 待结算分成（含冲回抵扣） */
  accrued: z.string(),
  settled: z.string(),
  /** 近 12 个月（含本月），新月份在前 */
  months: z.array(ResellerMonthSchema),
});
export type ResellerPortalSummary = z.infer<typeof ResellerPortalSummarySchema>;

export const ResellerCustomerSchema = z.object({
  id: z.string(),
  email: z.string(),
  email_verified: z.boolean(),
  status: z.enum(['active', 'suspended', 'closed']),
  registered_at: z.string(),
  bound_at: z.string().nullable(),
  currency: CurrencySchema,
  available: z.string(),
  /** 累计实付 */
  settled_total: z.string(),
  charged_30d: z.string(),
  tasks_30d: z.number(),
  last_task_at: z.string().nullable(),
});
export type ResellerCustomer = z.infer<typeof ResellerCustomerSchema>;

/** 渠道看到的消费 / 分成明细（每个已结算任务一条；不含平台成本） */
export const ResellerUsageSchema = z.object({
  id: z.string(),
  kind: z.enum(COMMISSION_KINDS),
  task_id: z.string().nullable(),
  customer_id: z.string(),
  customer_email: z.string(),
  model: z.string().nullable(),
  charged: z.string(),
  wholesale: z.string(),
  amount: z.string(),
  discount_pct: z.number(),
  currency: CurrencySchema,
  status: z.enum(COMMISSION_STATUSES),
  created_at: z.string(),
  settled_at: z.string().nullable(),
});
export type ResellerUsage = z.infer<typeof ResellerUsageSchema>;

export const ResellerSettlementSchema = z.object({
  id: z.string(),
  method: z.enum(RESELLER_SETTLEMENT_METHODS),
  amount: z.string(),
  currency: CurrencySchema,
  commission_count: z.number(),
  reference_no: z.string().nullable(),
  note: z.string().nullable(),
  created_at: z.string(),
});
export type ResellerSettlement = z.infer<typeof ResellerSettlementSchema>;

/** 当前用户所属渠道后台（/portal/me 的 reseller 字段）；不是渠道成员时为 null */
export const MeResellerSchema = z.object({ id: z.string(), name: z.string(), role: z.enum(RESELLER_ROLES) });
export type MeReseller = z.infer<typeof MeResellerSchema>;

// ───────────── 管理后台 ─────────────

export const AdminResellerMemberSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  role: z.enum(RESELLER_ROLES),
  created_at: z.string(),
});
export type AdminResellerMember = z.infer<typeof AdminResellerMemberSchema>;

export const AdminResellerSchema = ResellerInfoSchema.extend({
  contact_name: z.string().nullable(),
  contact_info: z.string().nullable(),
  note: z.string().nullable(),
  customers: z.number(),
  members: z.array(AdminResellerMemberSchema),
  accrued: z.string(),
  accrued_count: z.number(),
  settled: z.string(),
  charged_30d: z.string(),
  amount_30d: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AdminReseller = z.infer<typeof AdminResellerSchema>;

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

export const CreateResellerSchema = z.object({
  name: z.string().trim().min(1).max(64),
  /** 不填则自动生成 */
  code: z.string().trim().max(32).optional(),
  discount_pct: DiscountPctSchema,
  model_discounts: z
    .array(ModelDiscountSchema)
    .max(50)
    .refine((list) => new Set(list.map((m) => m.model)).size === list.length, '同一模型只能设置一个折扣')
    .default([]),
  contact_name: optionalText(64),
  contact_info: optionalText(200),
  note: optionalText(500),
});
export type CreateReseller = z.infer<typeof CreateResellerSchema>;

/** 推广码创建后不可修改（已发出的链接会失效） */
export const UpdateResellerSchema = CreateResellerSchema.omit({ code: true })
  .partial()
  .extend({
    status: z.enum(RESELLER_STATUSES).optional(),
    // CreateResellerSchema.model_discounts 带 .default([])；.partial() 下省略该字段仍会回填 []，
    // 使仅改 status 的部分更新把既有按模型折扣清空。这里重声明为"可省略、无默认"：
    // 省略时值为 undefined，updateReseller 便不会触碰 modelDiscounts（service.ts:131）。
    model_discounts: z
      .array(ModelDiscountSchema)
      .max(50)
      .refine((list) => new Set(list.map((m) => m.model)).size === list.length, '同一模型只能设置一个折扣')
      .optional(),
  });
export type UpdateReseller = z.infer<typeof UpdateResellerSchema>;

export const AddResellerMemberSchema = z.object({
  email: z.email(),
  role: z.enum(RESELLER_ROLES).default('owner'),
});
export type AddResellerMember = z.infer<typeof AddResellerMemberSchema>;

/** 把用户改挂到某渠道（null = 改为平台直客） */
export const AssignUserResellerSchema = z.object({ reseller_id: z.string().nullable() });
export type AssignUserReseller = z.infer<typeof AssignUserResellerSchema>;

export const AdminResellerCommissionSchema = ResellerUsageSchema.extend({
  reseller_id: z.string(),
  reseller_name: z.string(),
  /** 该任务的上游成本（平台视角，用于核对折扣是否低于成本） */
  upstream_cost: z.string().nullable(),
  settlement_id: z.string().nullable(),
  note: z.string().nullable(),
  reversed_at: z.string().nullable(),
});
export type AdminResellerCommission = z.infer<typeof AdminResellerCommissionSchema>;

export const AdminResellerSettlementSchema = ResellerSettlementSchema.extend({
  reseller_id: z.string(),
  reseller_name: z.string(),
  ledger_entry_id: z.string().nullable(),
  operator: z.string().nullable(),
});
export type AdminResellerSettlement = z.infer<typeof AdminResellerSettlementSchema>;

export const SettleResellerSchema = z.object({
  method: z.enum(RESELLER_SETTLEMENT_METHODS),
  /** 线下打款的转账单号 */
  reference_no: z.string().trim().max(64).optional(),
  note: z.string().trim().max(500).optional(),
  /** 客户端生成的幂等键：同键重放返回同一次结算 */
  idempotency_key: z.string().min(8).max(64),
});
export type SettleReseller = z.infer<typeof SettleResellerSchema>;

/** 用户详情里的渠道信息 */
export const AdminUserResellerSchema = z.object({
  /** 作为客户归属的渠道 */
  customer_of: z
    .object({ id: z.string(), code: z.string(), name: z.string(), status: z.enum(RESELLER_STATUSES), bound_at: z.string().nullable() })
    .nullable(),
  /** 作为渠道后台成员 */
  member_of: z.object({ id: z.string(), name: z.string(), role: z.enum(RESELLER_ROLES) }).nullable(),
});
export type AdminUserReseller = z.infer<typeof AdminUserResellerSchema>;
